// ===========================
// MODAL MANAGEMENT
// ===========================

import { CONFIG } from './config.js';
import { generateId } from './utils.js';
import { Entity, Association, Attribute, Connection } from './models.js';
import { UpdateEntityCommand, UpdateAssociationCommand, DeleteAssociationCommand, UpdateConnectionCommand, UpdateTextCommand } from './commands.js';
import { parseCardinality, cardinalityFromInputs } from './cardinality.js';
import { validateSqlAttribute, CHARACTER_TYPES, NUMBER_TYPES, defaultKindFor } from './sql-options.js';

const escapeHTML = value => String(value ?? '').replace(/[&<>'"]/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
}[character]));

export class ModalManager {
    constructor(state, renderer) {
        this.state = state;
        this.renderer = renderer;
    }

    validateAttributeEditors(container, list) {
        for (const attribute of container.attributes) {
            try { validateSqlAttribute(attribute); }
            catch (error) {
                const row = [...list.querySelectorAll('.attribute-editor')].find(row => row.dataset.attributeId === attribute.id);
                if (row) { row.querySelector('.attr-sql-error').textContent = error.message; row.querySelector('input:invalid, .attr-length, .attr-precision')?.focus(); }
                return false;
            }
        }
        return true;
    }

    openEntityModal(entityId) {
        const entity = this.state.getEntity(entityId);
        if (!entity) return;

        const modal = document.getElementById('entity-modal');
        modal.classList.add('active');
        modal.setAttribute('aria-hidden', 'false');

        const originalEntity = Entity.fromJSON(entity.toJSON());

        this.renderEntityModalContent(entity);

        document.getElementById('entity-modal-cancel').onclick = () => {
            Object.assign(entity, originalEntity);
            modal.classList.remove('active');
            modal.setAttribute('aria-hidden', 'true');
            this.renderer.render();
        };

        document.getElementById('entity-modal-confirm').onclick = () => {
            if (!this.validateAttributeEditors(entity, document.getElementById('entity-modal-attributes'))) return;
            if (!entity.name.trim()) {
                alert('Le nom de l\'entité ne peut pas être vide.');
                return;
            }

            const hasPK = entity.attributes.some(a => a.isPK);
            if (!hasPK) {
                if (!confirm('Aucune clé primaire définie. Continuer quand même?')) {
                    return;
                }
            }

            const names = entity.attributes.map(a => a.name.toLowerCase());
            if (entity.attributes.some(attribute => !attribute.name.trim())) {
                alert('Chaque attribut doit avoir un nom.');
                return;
            }
            const duplicates = names.filter((n, i) => names.indexOf(n) !== i);
            if (duplicates.length > 0) {
                alert('Noms d\'attributs en double : ' + duplicates.join(', '));
                return;
            }

            this.state.executeCommand(
                new UpdateEntityCommand(this.state, entity.id, originalEntity, Entity.fromJSON(entity.toJSON()))
            );

            modal.classList.remove('active');
            modal.setAttribute('aria-hidden', 'true');
            this.renderer.render();
            if (window.app) window.app.updatePropertiesPanel();
        };

        document.getElementById('entity-modal-name').focus();
    }

    renderEntityModalContent(entity) {
        const nameInput = document.getElementById('entity-modal-name');
        nameInput.value = entity.name;
        nameInput.oninput = () => { entity.name = nameInput.value; };

        const attrList = document.getElementById('entity-modal-attributes');
        attrList.innerHTML = '';

        entity.attributes.forEach((attr, index) => {
            const attrEl = this.createAttributeEditor(entity, attr, index);
            attrList.appendChild(attrEl);
        });

        document.getElementById('entity-add-attribute').onclick = () => {
            const newAttr = new Attribute(generateId('attr'), 'nouvel_attribut', 'VARCHAR');
            entity.attributes.push(newAttr);
            this.renderEntityModalContent(entity);
        };
    }

    createAttributeEditor(container, attr, index) {
        const div = document.createElement('div');
        div.className = 'attribute-editor';
        div.dataset.attributeId = attr.id;

        div.innerHTML = `
            <div class="attribute-editor-header">
                <span class="attribute-order">#${index + 1}</span>
                <input type="text" class="attr-name-input" value="${escapeHTML(attr.name)}" placeholder="Nom" aria-label="Nom de l’attribut ${index + 1}" />
                <button type="button" class="btn-icon btn-move-up" title="Monter" aria-label="Monter l’attribut" ${index === 0 ? 'disabled' : ''}>↑</button>
                <button type="button" class="btn-icon btn-move-down" title="Descendre" aria-label="Descendre l’attribut" ${index === container.attributes.length - 1 ? 'disabled' : ''}>↓</button>
                <button type="button" class="btn-icon btn-delete-attr" title="Supprimer" aria-label="Supprimer l’attribut">×</button>
            </div>
            <div class="attribute-editor-body">
                <div class="attr-row">
                    <label>Type:</label>
                    <select class="attr-type-select" aria-label="Type de l’attribut ${index + 1}">
                        ${CONFIG.SQL_TYPES.map(t =>
                            `<option value="${t}" ${t === attr.type ? 'selected' : ''}>${t}</option>`
                        ).join('')}
                    </select>
                </div>
                <div class="attr-row attr-checkboxes">
                    <label><input type="checkbox" class="attr-pk" ${attr.isPK ? 'checked' : ''}> PK</label>
                    <label><input type="checkbox" class="attr-uq" ${attr.isUQ ? 'checked' : ''}> UQ</label>
                    <label><input type="checkbox" class="attr-null" ${attr.isNull ? 'checked' : ''}> NULL</label>
                </div>
                <div class="attr-row">
                    <label>DEFAULT:</label>
                    <input type="text" class="attr-default" value="${escapeHTML(attr.defaultValue || '')}" placeholder="Valeur sans guillemets" aria-label="Valeur par défaut" maxlength="160" />
                </div>
                <div class="attr-sql-options">
                    <label class="attr-length-label">Longueur <input class="attr-length" type="number" min="1" max="4000" step="1" value="${escapeHTML(attr.length ?? '')}" placeholder="${attr.type === 'CHAR' ? 1 : 255}" /></label>
                    <label class="attr-precision-label">Précision <input class="attr-precision" type="number" min="1" max="38" step="1" value="${escapeHTML(attr.precision ?? '')}" placeholder="${attr.type === 'NUMBER' ? 38 : 18}" /></label>
                    <label class="attr-scale-label">Décimales <input class="attr-scale" type="number" min="0" max="38" step="1" value="${escapeHTML(attr.scale ?? '')}" placeholder="${attr.type === 'NUMBER' ? 0 : 2}" /></label>
                    <label class="attr-identity-label"><input class="attr-identity" type="checkbox" ${attr.identity ? 'checked' : ''} /> Auto-incrément</label>
                    <label>Défaut <select class="attr-default-kind" aria-label="Mode de valeur par défaut"><option value="literal">Valeur</option><option value="expression" ${defaultKindFor(attr) === 'expression' ? 'selected' : ''}>Expression Oracle</option></select></label>
                    <p class="attr-sql-error form-error" role="alert"></p>
                </div>
                <div class="attr-enum-section" style="display: ${attr.type === 'ENUM' || attr.type === 'SET' ? 'block' : 'none'};">
                    <label>Valeurs (ENUM/SET):</label>
                    <div class="attr-enum-tags"></div>
                    <input type="text" class="attr-enum-input" placeholder="Ajouter une valeur (Entrée)" />
                </div>
            </div>
        `;

        // Event handlers
        const nameInput = div.querySelector('.attr-name-input');
        nameInput.oninput = () => { attr.name = nameInput.value; };

        const typeSelect = div.querySelector('.attr-type-select');
        const updateSqlOptions = () => {
            div.querySelector('.attr-length-label').hidden = !CHARACTER_TYPES.includes(attr.type);
            for (const key of ['precision', 'scale']) div.querySelector(`.attr-${key}-label`).hidden = !['DECIMAL', 'NUMBER'].includes(attr.type);
            div.querySelector('.attr-identity-label').hidden = container.type !== 'entity' || !NUMBER_TYPES.includes(attr.type);
            div.querySelector('.attr-length').placeholder = attr.type === 'ENUM' ? 'Automatique' : attr.type === 'CHAR' ? '1' : '255';
            div.querySelector('.attr-precision').placeholder = attr.type === 'NUMBER' ? '38' : '18';
            div.querySelector('.attr-scale').placeholder = attr.type === 'NUMBER' ? '0' : '2';
        };
        updateSqlOptions();
        typeSelect.onchange = () => {
            attr.type = typeSelect.value;
            attr.defaultKind = div.querySelector('.attr-default-kind').value;
            for (const key of ['length', 'precision', 'scale', 'identity']) delete attr[key];
            for (const key of ['length', 'precision', 'scale']) div.querySelector(`.attr-${key}`).value = '';
            div.querySelector('.attr-identity').checked = false;
            updateSqlOptions();
            div.querySelector('.attr-enum-section').style.display =
                (attr.type === 'ENUM' || attr.type === 'SET') ? 'block' : 'none';
        };

        for (const key of ['length', 'precision', 'scale']) div.querySelector(`.attr-${key}`).oninput = event => {
            if (event.target.value === '' && !event.target.validity.badInput) delete attr[key];
            else attr[key] = event.target.valueAsNumber;
            div.querySelector('.attr-sql-error').textContent = '';
        };
        div.querySelector('.attr-identity').onchange = event => { attr.identity = event.target.checked; };
        div.querySelector('.attr-default-kind').onchange = event => { attr.defaultKind = event.target.value; };

        const pkCheck = div.querySelector('.attr-pk');
        pkCheck.onchange = () => { attr.isPK = pkCheck.checked; };

        const uqCheck = div.querySelector('.attr-uq');
        uqCheck.onchange = () => { attr.isUQ = uqCheck.checked; };

        const nullCheck = div.querySelector('.attr-null');
        nullCheck.onchange = () => { attr.isNull = nullCheck.checked; };

        const defaultInput = div.querySelector('.attr-default');
        defaultInput.oninput = () => { attr.defaultValue = defaultInput.value; attr.defaultKind = div.querySelector('.attr-default-kind').value; };

        // ENUM/SET values
        const enumTags = div.querySelector('.attr-enum-tags');
        const enumInput = div.querySelector('.attr-enum-input');

        const renderEnumTags = () => {
            enumTags.innerHTML = attr.enumValues.map((val, i) =>
                `<span class="enum-tag">${escapeHTML(val)} <button type="button" class="enum-tag-remove" aria-label="Supprimer ${escapeHTML(val)}" data-index="${i}">×</button></span>`
            ).join('');

            enumTags.querySelectorAll('.enum-tag-remove').forEach(btn => {
                btn.onclick = () => {
                    attr.enumValues.splice(parseInt(btn.dataset.index), 1);
                    renderEnumTags();
                };
            });
        };

        renderEnumTags();

        enumInput.onkeydown = (e) => {
            if (e.key === 'Enter' && enumInput.value.trim()) {
                attr.enumValues.push(enumInput.value.trim());
                enumInput.value = '';
                renderEnumTags();
                e.preventDefault();
            }
        };

        // Move buttons
        div.querySelector('.btn-move-up').onclick = () => {
            if (index > 0) {
                [container.attributes[index], container.attributes[index - 1]] =
                [container.attributes[index - 1], container.attributes[index]];
                if (container.type === 'entity') {
                    this.renderEntityModalContent(container);
                } else {
                    this.renderAssociationModalContent(container);
                }
            }
        };

        div.querySelector('.btn-move-down').onclick = () => {
            if (index < container.attributes.length - 1) {
                [container.attributes[index], container.attributes[index + 1]] =
                [container.attributes[index + 1], container.attributes[index]];
                if (container.type === 'entity') {
                    this.renderEntityModalContent(container);
                } else {
                    this.renderAssociationModalContent(container);
                }
            }
        };

        // Delete button
        div.querySelector('.btn-delete-attr').onclick = () => {
            if (confirm('Supprimer cet attribut?')) {
                container.attributes.splice(index, 1);
                if (container.type === 'entity') {
                    this.renderEntityModalContent(container);
                } else {
                    this.renderAssociationModalContent(container);
                }
            }
        };

        return div;
    }

    openAssociationModal(assocId) {
        const current = this.state.getAssociation(assocId);
        if (!current) return;

        const modal = document.getElementById('association-modal');
        modal.classList.add('active');
        modal.setAttribute('aria-hidden', 'false');

        const originalAssoc = Association.fromJSON(current.toJSON());
        const originalConnections = this.state.getConnectionsForAssociation(assocId).map(c => Connection.fromJSON(c.toJSON()));
        const assoc = Association.fromJSON(current.toJSON());
        this.associationDraft = {
            association: assoc,
            connections: originalConnections.map(c => Connection.fromJSON(c.toJSON())),
            inputs: new Map(originalConnections.map(connection => [connection.id, this.createConnectionInputs(connection)]))
        };

        const close = () => {
            this.associationDraft = null;
            modal.classList.remove('active');
            modal.setAttribute('aria-hidden', 'true');
            this.renderer.render();
        };

        this.renderAssociationModalContent(assoc);

        document.getElementById('assoc-modal-cancel').onclick = close;

        document.getElementById('assoc-modal-confirm').onclick = () => {
            if (!this.validateAttributeEditors(assoc, document.getElementById('assoc-modal-attributes'))) return;
            if (!assoc.name.trim()) {
                alert('Le nom de l\'association ne peut pas être vide.');
                return;
            }

            if (assoc.attributes.some(attribute => !attribute.name.trim())) {
                alert('Chaque attribut doit avoir un nom.');
                return;
            }
            let invalidInput = null;
            document.querySelectorAll('#assoc-modal-connections .connection-item').forEach(row => {
                const input = this.associationDraft.inputs.get(row.dataset.connId);
                input.showError = true;
                const error = this.updateCardinalityFeedback(row, input);
                if (error && !invalidInput) invalidInput = row.querySelector(`[data-bound="${error.field}"]`);
            });
            if (invalidInput) {
                invalidInput.focus();
                return;
            }
            const newConnections = this.associationDraft.connections.map(connection => Connection.fromJSON({
                ...connection.toJSON(),
                label: connection.label.trim(),
                cardinality: cardinalityFromInputs(this.associationDraft.inputs.get(connection.id)).value
            }));
            this.state.executeCommand(new UpdateAssociationCommand(
                this.state,
                assoc.id,
                originalAssoc,
                Association.fromJSON(assoc.toJSON()),
                originalConnections,
                newConnections
            ));

            close();
            if (window.app) window.app.updatePropertiesPanel();
        };

        document.getElementById('assoc-modal-delete').onclick = () => {
            if (confirm('Supprimer cette association et toutes ses connexions?')) {
                this.state.executeCommand(new DeleteAssociationCommand(this.state, current));
                close();
                if (window.app) window.app.updatePropertiesPanel();
            }
        };

        document.getElementById('assoc-modal-name').focus();
    }

    updateCardinalityFeedback(row, inputs) {
        let parsed;
        let error;
        try {
            parsed = cardinalityFromInputs(inputs);
        } catch (failure) {
            error = failure;
        }
        row.querySelector('.cardinality-result').textContent = parsed ? parsed.value : 'À compléter';
        const errorElement = row.querySelector('.cardinality-error');
        errorElement.textContent = inputs.showError && error ? error.message : '';
        errorElement.hidden = !errorElement.textContent;
        row.querySelectorAll('[data-bound]').forEach(input => {
            input.setAttribute('aria-invalid', String(Boolean(inputs.showError && error?.field === input.dataset.bound)));
        });
        row.querySelectorAll('[data-preset]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.dataset.preset === parsed?.value));
        });
        return error;
    }

    createConnectionInputs(connection) {
        const parsed = parseCardinality(connection.cardinality);
        return { minimum: String(parsed.minimum), maximum: String(parsed.maximum ?? Math.max(1, parsed.minimum)),
            unlimited: parsed.maximum === null, showError: false };
    }

    renderConnectionEditors(connList, connections, inputDrafts, onDelete = null) {

        connList.innerHTML = connections.map((conn, index) => {
            const entity = this.state.getEntity(conn.entityId);
            const inputs = inputDrafts.get(conn.id);
            const errorId = `cardinality-error-${index}`;
            return `
                <div class="connection-item" role="group" aria-label="Connexion avec ${escapeHTML(entity?.name || 'entité inconnue')}" data-conn-id="${escapeHTML(conn.id)}">
                    <strong>${escapeHTML(entity?.name || 'Entité inconnue')}</strong>
                    <label class="connection-role"><span>Rôle métier</span><input type="text" maxlength="160" class="conn-label-input" aria-label="Rôle de la connexion" data-conn-id="${escapeHTML(conn.id)}" value="${escapeHTML(conn.label || '')}" placeholder="Par exemple : emprunte" /></label>
                    <button type="button" class="btn-icon btn-delete-conn" aria-label="Supprimer la connexion" data-conn-id="${escapeHTML(conn.id)}">×</button>
                    <div class="cardinality-fields">
                        <label>Minimum<input type="text" inputmode="numeric" autocomplete="off" maxlength="160" data-bound="minimum" aria-describedby="${errorId}" value="${escapeHTML(inputs.minimum)}"></label>
                        <label>Maximum<input type="text" inputmode="numeric" autocomplete="off" maxlength="160" data-bound="maximum" aria-describedby="${errorId}" value="${inputs.unlimited ? '' : escapeHTML(inputs.maximum)}" ${inputs.unlimited ? 'disabled placeholder="n"' : ''}></label>
                        <label class="cardinality-unlimited"><input type="checkbox" class="conn-unlimited" ${inputs.unlimited ? 'checked' : ''}> Sans limite (n)</label>
                    </div>
                    <div class="cardinality-presets" role="group" aria-label="Valeurs courantes">
                        <span>Valeurs courantes</span>
                        ${CONFIG.CARDINALITY_OPTIONS.map(option => `<button type="button" data-preset="${option.value}" title="${escapeHTML(option.label)}" aria-pressed="false">${option.value}</button>`).join('')}
                        <output class="cardinality-result" aria-label="Cardinalité"></output>
                    </div>
                    <p id="${errorId}" class="form-error cardinality-error" role="alert" hidden></p>
                </div>
            `;
        }).join('');

        if (connections.length === 0) {
            connList.innerHTML = '<p class="no-connections">Aucune connexion. Utilisez l\'outil Connexion pour relier cette association à des entités.</p>';
        }

        // Event handlers for connections
        connList.querySelectorAll('.connection-item').forEach(row => {
            const inputs = inputDrafts.get(row.dataset.connId);
            row.querySelectorAll('[data-bound]').forEach(input => {
                input.oninput = () => {
                    inputs[input.dataset.bound] = input.value;
                    this.updateCardinalityFeedback(row, inputs);
                };
                input.onblur = () => {
                    inputs.showError = true;
                    this.updateCardinalityFeedback(row, inputs);
                };
            });
            row.querySelector('.conn-unlimited').onchange = event => {
                inputs.unlimited = event.target.checked;
                const maximum = row.querySelector('[data-bound="maximum"]');
                maximum.disabled = inputs.unlimited;
                maximum.value = inputs.unlimited ? '' : inputs.maximum;
                maximum.placeholder = inputs.unlimited ? 'n' : '';
                this.updateCardinalityFeedback(row, inputs);
            };
            row.querySelectorAll('[data-preset]').forEach(button => {
                button.onclick = () => {
                    const parsed = parseCardinality(button.dataset.preset);
                    inputs.minimum = String(parsed.minimum);
                    inputs.unlimited = parsed.maximum === null;
                    if (parsed.maximum !== null) inputs.maximum = String(parsed.maximum);
                    row.querySelector('[data-bound="minimum"]').value = inputs.minimum;
                    const maximum = row.querySelector('[data-bound="maximum"]');
                    maximum.value = inputs.unlimited ? '' : inputs.maximum;
                    maximum.disabled = inputs.unlimited;
                    maximum.placeholder = inputs.unlimited ? 'n' : '';
                    row.querySelector('.conn-unlimited').checked = inputs.unlimited;
                    this.updateCardinalityFeedback(row, inputs);
                };
            });
            this.updateCardinalityFeedback(row, inputs);
        });

        connList.querySelectorAll('.conn-label-input').forEach(input => {
            input.oninput = () => {
                const conn = connections.find(connection => connection.id === input.dataset.connId);
                if (conn) conn.label = input.value;
            };
        });

        connList.querySelectorAll('.btn-delete-conn').forEach(button => {
            if (!onDelete) { button.remove(); return; }
            button.onclick = () => {
                if (confirm('Supprimer cette connexion ?')) onDelete(button.dataset.connId);
            };
        });
    }

    openConnectionModal(id, focusField = 'minimum') {
        const current = this.state.getConnection(id);
        if (!current || document.querySelector('.modal-overlay.active')) return;
        const draft = Connection.fromJSON(current.toJSON());
        const inputs = this.createConnectionInputs(current);
        const modal = document.getElementById('connection-modal');
        const body = document.getElementById('connection-modal-fields');
        document.getElementById('connection-modal-context').textContent =
            `${this.state.getAssociation(current.associationId)?.name} — ${this.state.getEntity(current.entityId)?.name}`;
        this.renderConnectionEditors(body, [draft], new Map([[id, inputs]]));
        const close = () => {
            modal.classList.remove('active'); modal.setAttribute('aria-hidden', 'true');
            this.renderer.render(); this.renderer.container.focus({ preventScroll: true });
        };
        document.getElementById('connection-modal-cancel').onclick = close;
        document.getElementById('connection-modal-close').onclick = close;
        document.getElementById('connection-modal-confirm').onclick = () => {
            inputs.showError = true;
            const error = this.updateCardinalityFeedback(body, inputs);
            if (error) { body.querySelector(`[data-bound="${error.field}"]`).focus(); return; }
            if (!this.state.getConnection(id)) { close(); return; }
            const next = { cardinality: cardinalityFromInputs(inputs).value, label: draft.label.trim() };
            if (next.cardinality !== current.cardinality || next.label !== current.label) {
                this.state.executeCommand(new UpdateConnectionCommand(this.state, id, current, next));
            }
            close();
        };
        window.app.openModal('connection-modal');
        body.querySelector(focusField === 'role' ? '.conn-label-input' : '[data-bound="minimum"]').focus();
    }

    openTextModal(id, { isNew = false, text = null } = {}) {
        const current = text || this.state.getText(id);
        if (!current) return;
        const modal = document.getElementById('text-modal');
        const input = document.getElementById('text-content');
        const size = document.getElementById('text-size');
        const error = document.getElementById('text-error');
        const draft = { text: current.text, fontSize: current.fontSize, bold: current.bold, italic: current.italic, underline: current.underline };
        input.value = draft.text; size.value = draft.fontSize; error.textContent = '';
        const preview = () => {
            input.style.fontWeight = draft.bold ? '700' : '400';
            input.style.fontStyle = draft.italic ? 'italic' : 'normal';
            input.style.textDecoration = draft.underline ? 'underline' : 'none';
            input.style.fontSize = `${Math.min(36, Math.max(10, Number(size.value) || 18))}px`;
        };
        for (const key of ['bold', 'italic', 'underline']) {
            const button = document.getElementById(`text-${key}`);
            button.setAttribute('aria-pressed', String(draft[key]));
            button.onclick = () => { draft[key] = !draft[key]; button.setAttribute('aria-pressed', String(draft[key])); preview(); };
        }
        size.oninput = preview;
        preview();
        const close = () => {
            window.app.closeModal('text-modal'); this.renderer.container.focus({ preventScroll: true });
        };
        document.getElementById('text-modal-cancel').onclick = close;
        document.getElementById('text-modal-close').onclick = close;
        document.getElementById('text-modal-confirm').onclick = () => {
            if (!input.value.trim()) { error.textContent = 'Ajoutez un texte.'; input.focus(); return; }
            if (!/^\d+$/.test(size.value) || Number(size.value) < 10 || Number(size.value) > 96) {
                error.textContent = 'Choisissez une taille entière entre 10 et 96.'; size.focus(); return;
            }
            Object.assign(draft, { text: input.value.trim(), fontSize: Number(size.value) });
            const command = new UpdateTextCommand(this.state, id, isNew ? null : current.toJSON(), { ...current.toJSON(), ...draft });
            if (isNew || JSON.stringify(current.toJSON()) !== JSON.stringify(command.next)) this.state.executeCommand(command);
            isNew = false;
            this.renderer.render(); close();
        };
        window.app.openModal('text-modal');
        input.focus();
    }

    renderAssociationModalContent(assoc) {
        const nameInput = document.getElementById('assoc-modal-name');
        nameInput.value = assoc.name;
        nameInput.oninput = () => { assoc.name = nameInput.value; };

        this.renderConnectionEditors(document.getElementById('assoc-modal-connections'),
            this.associationDraft.connections, this.associationDraft.inputs, id => {
                this.associationDraft.connections = this.associationDraft.connections.filter(c => c.id !== id);
                this.associationDraft.inputs.delete(id);
                this.renderAssociationModalContent(assoc);
            });

        // Render attributes
        const attrList = document.getElementById('assoc-modal-attributes');
        attrList.innerHTML = '';

        assoc.attributes.forEach((attr, index) => {
            const attrEl = this.createAttributeEditor(assoc, attr, index);
            attrList.appendChild(attrEl);
        });

        if (assoc.attributes.length === 0) {
            attrList.innerHTML = '<p class="no-attributes">Aucun attribut.</p>';
        }

        document.getElementById('assoc-add-attribute').onclick = () => {
            const newAttr = new Attribute(generateId('attr'), 'nouvel_attribut', 'VARCHAR');
            assoc.attributes.push(newAttr);
            this.renderAssociationModalContent(assoc);
        };
    }
}
